/**
 * A 30-line DOM helper instead of a framework.
 *
 * Six boards and a table do not need a virtual DOM, and every dependency is a
 * licence and a supply-chain surface. One rule matters here: nothing in this
 * file ever writes a `style=` attribute. The site's CSP is a bare
 * `style-src 'self'` with no `unsafe-inline`, so dynamic geometry is set
 * through CSSOM (`el.style.setProperty`) — assignment survives that policy,
 * a markup attribute does not.
 */

type Attrs = Record<string, string | number | boolean | undefined | null | EventListener>;
export type Child = Node | string | number | null | undefined | false;

export function h<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  attrs: Attrs = {},
  ...children: Child[]
): HTMLElementTagNameMap[K] {
  const el = document.createElement(tag);
  for (const [key, value] of Object.entries(attrs)) {
    if (value === undefined || value === null || value === false) continue;
    if (key.startsWith("on") && typeof value === "function") {
      el.addEventListener(key.slice(2).toLowerCase(), value as EventListener);
    } else if (key === "class") {
      el.className = String(value);
    } else if (key === "text") {
      el.textContent = String(value);
    } else {
      el.setAttribute(key, value === true ? "" : String(value));
    }
  }
  append(el, children);
  return el;
}

const SVG_NS = "http://www.w3.org/2000/svg";

/**
 * {@link h} for the SVG namespace — the rating chart's only drawing primitive.
 *
 * `document.createElement` produces an HTMLUnknownElement for `<polyline>`, so a
 * chart built with {@link h} lays out as inline text and paints nothing. The
 * differences from {@link h} are the namespace and `class`: `SVGElement.className`
 * is a read-only `SVGAnimatedString`, so the class has to go through
 * `setAttribute` like every other attribute here.
 *
 * The file's one rule holds unchanged: geometry goes in ATTRIBUTES (`points`,
 * `x`, `cx`, `d`) and colour comes from a stylesheet class, because the site's
 * CSP is a bare `style-src 'self'` and an inline `style=` would be dropped.
 */
export function svg<K extends keyof SVGElementTagNameMap>(
  tag: K,
  attrs: Attrs = {},
  ...children: Child[]
): SVGElementTagNameMap[K] {
  const el = document.createElementNS(SVG_NS, tag);
  for (const [key, value] of Object.entries(attrs)) {
    if (value === undefined || value === null || value === false) continue;
    if (key.startsWith("on") && typeof value === "function") {
      el.addEventListener(key.slice(2).toLowerCase(), value as EventListener);
    } else if (key === "text") {
      el.textContent = String(value);
    } else {
      el.setAttribute(key, value === true ? "" : String(value));
    }
  }
  append(el, children);
  return el;
}

/**
 * The `href` for an external link whose address came from DATA (a producer, a
 * feed, a config), or null when it must not become a link (#3117).
 *
 * The one scheme check on the site: only `http:` and `https:` pass. A
 * `javascript:`/`data:` URL, a relative or protocol-relative path, or a string
 * `URL` cannot parse yields null, and the caller draws no link. The CSP
 * (`script-src 'self'`) already blocks `javascript:` navigation in enforcing
 * browsers; this is the second layer, so a feed is never the only thing between
 * a string and an `href`. Returns the trimmed input, not `URL.href`, so a link
 * reads exactly as its source published it.
 */
export function safeExternalHref(raw: string): string | null {
  const trimmed = raw.trim();
  if (trimmed.length === 0) return null;
  let parsed: URL;
  try {
    parsed = new URL(trimmed);
  } catch {
    return null;
  }
  return parsed.protocol === "https:" || parsed.protocol === "http:" ? trimmed : null;
}

export function append(parent: Element, children: Child[]): void {
  for (const child of children) {
    if (child === null || child === undefined || child === false) continue;
    parent.append(child instanceof Node ? child : document.createTextNode(String(child)));
  }
}

export function clear(el: Element): void {
  el.replaceChildren();
}

/**
 * `mm:ss`, or `h:mm:ss` past an hour. Null renders as an em dash.
 *
 * `hours: true` always writes the hour field (`0:05:07`): for a clock that sits
 * after a day count (`1d 0:05:07`, `live/round-state.ts` `clockCountdown`),
 * where `1d 5:07` would read as five hours.
 *
 * `calm: true` drops the seconds at or over an hour (`1:02`): the rail's clocks
 * (`ui/game-row.ts` `railClock`, owner 2026-09-25), which then change once a minute.
 */
export function formatClock(sec: number | null, opts: { hours?: boolean; calm?: boolean } = {}): string {
  if (sec === null) return "—";
  const s = Math.max(0, Math.trunc(sec));
  const h2 = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const ss = s % 60;
  const pad = (n: number) => String(n).padStart(2, "0");
  if (h2 > 0 && opts.calm === true) return `${h2}:${pad(m)}`;
  return h2 > 0 || opts.hours ? `${h2}:${pad(m)}:${pad(ss)}` : `${m}:${pad(ss)}`;
}

/**
 * Local time of day for a schedule row.
 *
 * A round with no announced start renders **"TBD"**, not a blank cell — the
 * app's word for `startsAfterPrevious` (`ScheduleMapper.kt:73`,
 * `ScheduleView.swift:295`). A blank reads as a rendering fault; TBD says the
 * organiser has not published a time yet.
 */
export function formatTime(ms: number | null): string {
  if (ms === null) return "TBD";
  return new Date(ms).toLocaleTimeString(SITE_LOCALE, { hour: "2-digit", minute: "2-digit", hourCycle: "h23" });
}

export function formatDay(ms: number | null): string {
  if (ms === null) return "";
  return new Date(ms).toLocaleDateString(SITE_LOCALE, { weekday: "long", month: "long", day: "numeric" });
}

/**
 * The site's one date style: day before month, 24-hour clock ("Fri 25 Sep, 10:15"). The
 * company is British and the site is written in British English, so the ORDER is fixed rather
 * than the reader's locale's; the TIME ZONE stays the reader's own, which is the part that
 * matters for "when does this start for me". Copy review 2026-09-24: the hub said "Fri, Sep 25"
 * two lines above a fixture list that said "Sat 5 Sep".
 */
export const SITE_LOCALE = "en-GB";

/** Trailing-edge debounce, for the search box. */
export function debounce<A extends unknown[]>(fn: (...args: A) => void, ms: number): (...args: A) => void {
  let t: ReturnType<typeof setTimeout> | undefined;
  return (...args: A) => {
    if (t) clearTimeout(t);
    t = setTimeout(() => fn(...args), ms);
  };
}

/**
 * Resolves once the browser has painted the current frame (the next animation frame, then a
 * task after it), or on the next task where there are no frames (tests, a worker). For reads a
 * page does not need for its first paint: started here, they no longer compete with that paint
 * for the network — which is what Lighthouse's simulated LCP charges them for.
 */
export function afterNextPaint(): Promise<void> {
  return new Promise((resolve) => {
    if (typeof requestAnimationFrame === "function") requestAnimationFrame(() => setTimeout(resolve, 0));
    else setTimeout(resolve, 0);
  });
}
